// src/types/index.ts — REESCRITO COMPLETO

// ─── Categorías universales de servicio ───────────────────────────────────────
export type ServiceCategory =
  | 'educacion' // Profesores, tutores, coaches
  | 'construccion' // Albañiles, pintores, electricistas
  | 'salud_bienestar' // Nutricionistas, entrenadores, psicólogos
  | 'profesional' // Abogados, contadores, consultores
  | 'tecnologia' // Programadores, diseñadores, IT
  | 'hogar' // Limpieza, jardinería, plomería
  | 'logistica' // Transporte, mudanzas, delivery
  | 'otro' // Todo lo demás

// ─── Tipo de resultado del acuerdo ───────────────────────────────────────────
// No todos los servicios tienen un "entregable digital"
export type AgreementOutcomeType =
  | 'sesion_completada' // Clase, consulta, sesión (ej: inglés, psicólogo)
  | 'obra_terminada' // Trabajo físico terminado (ej: pintura, plomería)
  | 'entregable_digital' // Archivo, documento, diseño
  | 'servicio_continuo' // Suscripción, retainer mensual
  | 'hito_alcanzado' // Parte de un proyecto mayor
  | 'acuerdo_mutuo' // Las partes confirman manualmente

// ─── Estado del acuerdo (universal) ──────────────────────────────────────────
export type AgreementStatus =
  | 'pending_payment' // Esperando pago
  | 'active' // Pago recibido, servicio en curso
  | 'outcome_registered' // Prestador registró el resultado
  | 'confirmed' // Cliente confirmó conformidad
  | 'completed' // Acuerdo finalizado, fondos liberados
  | 'revision_requested' // Cliente pidió revisión
  | 'disputed' // En disputa
  | 'refunded' // Devuelto al cliente
  | 'expired' // Venció sin acción

export interface ProviderProfile {
  id: string
  name: string
  email: string
  mp_connected: boolean
  mp_user_id: string | null
  created_at: string
}

export interface Agreement {
  id: string
  agreement_code: string // AGREE-XXXXXX
  title: string
  description: string | null
  category: ServiceCategory
  outcome_type: AgreementOutcomeType

  // Partes
  provider_name: string // "Prestador" — universal
  provider_email: string | null
  provider_phone: string | null
  client_email: string
  client_name: string | null

  // Económico
  amount: number
  currency: string
  commission_rate: number
  net_amount: number

  // Tiempo
  service_date: string | null // Cuándo se presta (puede ser rango)
  service_date_end: string | null
  deadline: string | null

  // Estado
  status: AgreementStatus
  escrow_state: string
  mp_payment_id: string | null

  // Timestamps
  created_at: string
  paid_at: string | null
  outcome_at: string | null
  confirmed_at: string | null
  completed_at: string | null
}

export interface AgreementOutcome {
  id: string
  agreement_id: string
  type: AgreementOutcomeType
  title: string
  description: string | null
  evidence_url: string | null // Foto, video, documento
  file_url: string | null
  notes: string | null
  created_at: string
}

// ─── Config por categoría ─────────────────────────────────────────────────────
export const CATEGORY_CONFIG: Record<
  ServiceCategory,
  {
    label: string
    icon: string
    examples: string[]
    outcomeTypes: AgreementOutcomeType[]
    placeholder: string
  }
> = {
  educacion: {
    label: 'Educación y formación',
    icon: '📚',
    examples: [
      'Clase de inglés',
      'Clases de matemáticas',
      'Coaching',
      'Tutoría',
    ],
    outcomeTypes: ['sesion_completada', 'hito_alcanzado', 'acuerdo_mutuo'],
    placeholder: 'Ej: 4 clases de inglés nivel intermedio, 1 hora c/u',
  },
  construccion: {
    label: 'Construcción y reformas',
    icon: '🏗️',
    examples: ['Pintura', 'Albañilería', 'Electricidad', 'Plomería'],
    outcomeTypes: ['obra_terminada', 'hito_alcanzado', 'acuerdo_mutuo'],
    placeholder: 'Ej: Pintura de living y comedor, 2 manos con látex',
  },
  salud_bienestar: {
    label: 'Salud y bienestar',
    icon: '💪',
    examples: ['Entrenamiento personal', 'Nutrición', 'Psicología', 'Yoga'],
    outcomeTypes: ['sesion_completada', 'servicio_continuo', 'acuerdo_mutuo'],
    placeholder: 'Ej: Plan nutricional personalizado + seguimiento 1 mes',
  },
  profesional: {
    label: 'Servicios profesionales',
    icon: '💼',
    examples: ['Asesoría legal', 'Contaduría', 'Consultoría', 'Arquitectura'],
    outcomeTypes: ['entregable_digital', 'hito_alcanzado', 'acuerdo_mutuo'],
    placeholder: 'Ej: Armado y presentación de declaración jurada anual',
  },
  tecnologia: {
    label: 'Tecnología y diseño',
    icon: '💻',
    examples: ['Desarrollo web', 'Diseño gráfico', 'Marketing', 'IT'],
    outcomeTypes: ['entregable_digital', 'hito_alcanzado', 'servicio_continuo'],
    placeholder: 'Ej: Diseño de logo + 3 variantes, entrega en 7 días',
  },
  hogar: {
    label: 'Hogar y mantenimiento',
    icon: '🏠',
    examples: ['Limpieza', 'Jardinería', 'Mudanzas', 'Reparaciones'],
    outcomeTypes: ['obra_terminada', 'sesion_completada', 'acuerdo_mutuo'],
    placeholder: 'Ej: Limpieza profunda de departamento 3 ambientes',
  },
  logistica: {
    label: 'Logística y transporte',
    icon: '🚚',
    examples: ['Fletes', 'Delivery', 'Mudanzas', 'Mensajería'],
    outcomeTypes: ['obra_terminada', 'sesion_completada', 'acuerdo_mutuo'],
    placeholder: 'Ej: Flete de muebles del centro al barrio norte',
  },
  otro: {
    label: 'Otro servicio',
    icon: '✨',
    examples: ['Cualquier servicio'],
    outcomeTypes: ['acuerdo_mutuo', 'hito_alcanzado', 'sesion_completada'],
    placeholder: 'Describí el servicio con el mayor detalle posible',
  },
}

export const OUTCOME_LABELS: Record<
  AgreementOutcomeType,
  {
    label: string
    description: string
    providerCTA: string // Qué dice el botón para el prestador
    clientCTA: string // Qué dice el botón para el cliente
  }
> = {
  sesion_completada: {
    label: 'Sesión completada',
    description: 'La sesión o encuentro se realizó',
    providerCTA: 'Marcar sesión como realizada',
    clientCTA: 'Confirmar que recibí el servicio',
  },
  obra_terminada: {
    label: 'Obra terminada',
    description: 'El trabajo físico está terminado',
    providerCTA: 'Marcar trabajo como terminado',
    clientCTA: 'Confirmar que el trabajo está listo',
  },
  entregable_digital: {
    label: 'Entregable enviado',
    description: 'El archivo o documento fue entregado',
    providerCTA: 'Registrar entrega',
    clientCTA: 'Confirmar que recibí el material',
  },
  servicio_continuo: {
    label: 'Período completado',
    description: 'El período de servicio finalizó',
    providerCTA: 'Cerrar período de servicio',
    clientCTA: 'Confirmar conformidad',
  },
  hito_alcanzado: {
    label: 'Hito alcanzado',
    description: 'Se completó esta etapa del proyecto',
    providerCTA: 'Registrar hito completado',
    clientCTA: 'Confirmar que el hito fue alcanzado',
  },
  acuerdo_mutuo: {
    label: 'Confirmación mutua',
    description: 'Ambas partes confirman el resultado',
    providerCTA: 'Marcar como completado',
    clientCTA: 'Confirmar conformidad',
  },
}

export const CURRENCIES = [
  { value: 'ARS', label: 'ARS — Peso argentino', symbol: '$' },
  { value: 'USD', label: 'USD — Dólar americano', symbol: 'US$' },
  { value: 'MXN', label: 'MXN — Peso mexicano', symbol: '$' },
  { value: 'COP', label: 'COP — Peso colombiano', symbol: '$' },
  { value: 'BRL', label: 'BRL — Real brasileño', symbol: 'R$' },
  { value: 'CLP', label: 'CLP — Peso chileno', symbol: '$' },
]

export const AGREEXA_COMMISSION = 0.05
